import { useAppDispatch, useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadModal from 'components/common/ImageUploadModal';
import MultiSelectDropdown from 'components/common/MultiSelectDropdown';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { DisplayControlGroup } from 'features/DisplayControlGroup/models';
import { DisplayControlGroupType, MENU_DISPLAY_TIMING, MENU_DISPLAY_TIMING_SUB } from 'features/MenuList/constants';
import { Overlay } from 'features/OverlayFunction/models/overlay';
import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Col, Form, Image, Modal, ModalProps, Row, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { getCurrency } from 'utils/common';
import { IMAGE_SIZES } from 'utils/constants';
import { Menu, MenuDetail, MenuProperty } from '../../MenuList/models/menu';
import { PROPERTY_LIMIT, PROPERTY_PROVIDE } from '../constants';
import menuDetailService from '../menuDetailService';
import { menuDetailActions, selectMenu } from '../menuDetailSlice';
import { MenuDetailView } from '../models/menuDetail';
import { MenuView } from '../models/viewMenu';
import './MenuDetail.scss';
import services from './service';

const MenuDetailModal = forwardRef<
  ModalProps,
  { onMenuModalClose?: () => void; menuCallBack?: (obj: {} | undefined) => void }
>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // selectMenu
  const menuSelector = useAppSelector(selectMenu);
  //ref
  let menuImgName = useRef('');
  // Promotional image refs
  let img1x2Name = useRef('');
  let img2x1Name = useRef('');
  let img2x2Name = useRef('');
  let img3x1Name = useRef('');
  let img3x2Name = useRef('');
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //state
  const [showModal, setShowModal] = useState(false);
  const [showImgUploadModal, setShowImgUploadModal] = useState(false);
  // Promotional image upload modal states
  const [showImg1x2UploadModal, setShowImg1x2UploadModal] = useState(false);
  const [showImg2x1UploadModal, setShowImg2x1UploadModal] = useState(false);
  const [showImg2x2UploadModal, setShowImg2x2UploadModal] = useState(false);
  const [showImg3x1UploadModal, setShowImg3x1UploadModal] = useState(false);
  const [showImg3x2UploadModal, setShowImg3x2UploadModal] = useState(false);
  // Current language for promotional image upload
  const [currentImgLang, setCurrentImgLang] = useState<string>('');
  const [menuView, setMenuView] = useState<MenuView>();
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [menuPropertiesActive, setMenuPropertiesActive] = useState<Array<MenuProperty>>();
  const [externalMenuSelected, setExternalMenuSelected] = useState<Menu>();
  const [menuDetails, setMenuDetails] = useState<MenuDetailView[]>();
  const [propertiesProvide, setPropertiesProvider] = useState<Array<MenuProperty>>([]);
  const [propertiesLimit, setPropertiesLimit] = useState<Array<MenuProperty>>([]);

  //external menu
  const [selectedMenuId, setSelectedMenuId] = useState<number>();

  useEffect(() => {
    if (menuSelector && Object.keys(menuSelector).length > 0) {
      setExternalMenuSelected(menuSelector);
    } else {
      setExternalMenuSelected(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuSelector]); // call from external page

  useEffect(() => {
    if (selectedMenuId && externalMenuSelected && Object.keys(externalMenuSelected).length > 0) {
      let menu_view = new MenuView(externalMenuSelected);
      setMenuView(menu_view);
      setSelectedMenu(menu_view.menu);
      menuImgName.current = menu_view.menu.img || '';
      setMenuPropertiesActive(menu_view.properties_active());
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMenuSelected, selectedMenuId]); // call from external page

  //useImperativeHandle
  useImperativeHandle(ref, () => ({
    // call from internal parent page
    openModal(menu_detail: MenuDetail, expired_date: Date = new Date()) {
      if (menu_detail) {
        if (menu_detail.menu.id !== selectedMenu?.id) {
          let menu_view = new MenuView(
            menu_detail.menu,
            menu_detail.start_date.toString(),
            menu_detail.end_date.toString()
          );
          getMenuDetails(menu_detail.menu.id, expired_date);
          setMenuView(menu_view);
          setSelectedMenu(menu_view.menu);
          menuImgName.current = menu_view.menu.img || '';
          setMenuPropertiesActive(menu_view.properties_active());
        }
        setShowModal(true);
      }
    },
    // call from external parent page
    openModalExternal(menu_id: number, data_date: Date = new Date()) {
      if (menu_id) {
        dispatch(menuDetailActions.fetchMenu({ menu_id: menu_id, expired_date: data_date })); //data_date format string '2023830'
        getMenuDetails(menu_id, data_date);
        setSelectedMenuId(menu_id);
      }
    },
    onReset() {
      setSelectedMenuId(undefined);
      setMenuView(undefined);
      setSelectedMenu(undefined);
      setMenuPropertiesActive(undefined);
      setExternalMenuSelected(undefined);
      dispatch(menuDetailActions.removeStateMenu({} as Menu));
    },
  }));

  const getMenuDetails = async (menu_id: number, str_date: Date) => {
    try {
      const response = await menuDetailService.getMenuDetails({ menu_id: menu_id, expired_date: str_date });
      setMenuDetails(response.data);
    } catch (error) {
      console.error('Error fetching CSV:', error);
    }
  };

  const handleChangeMenuAttribute = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const property_name = event.currentTarget.name;
    const value = event.currentTarget.value;
    if (selectedMenu) {
      const obj = {
        [property_name]: value,
      };
      setSelectedMenu({ ...selectedMenu, img: menuImgName.current, ...obj });
    }
  };

  useEffect(() => {
    if (menuPropertiesActive) {
      // Filter menuPropertiesActive by text and set to propertiesProvide
      const filteredProvide = menuPropertiesActive.filter(
        (prop) => PROPERTY_PROVIDE.includes(prop.text) && prop.text.trim() !== ''
      );
      setPropertiesProvider(filteredProvide);
      // Filter menuPropertiesActive by limit and set to propertiesLimit
      const filteredLimit = menuPropertiesActive.filter(
        (prop) => PROPERTY_LIMIT.includes(prop.text) && prop.text.trim() !== ''
      );
      setPropertiesLimit(filteredLimit);
    }
  }, [menuPropertiesActive]);

  const handleChangeServiceTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      if (event.currentTarget.value === '') {
        // Set to null for (非表示) option
        setSelectedMenu({
          ...selectedMenu,
          timing_id: null as unknown as number,
        });
      } else {
        setSelectedMenu({
          ...selectedMenu,
          timing_id: parseInt(event.currentTarget.value),
        });
      }
    }
  };

  const handleChangeWizard = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      if (event.currentTarget.value === '') {
        // Set to null for (非表示) option
        setSelectedMenu({
          ...selectedMenu,
          wizard_id: null as unknown as number,
        });
      } else {
        setSelectedMenu({
          ...selectedMenu,
          wizard_id: parseInt(event.currentTarget.value),
        });
      }
    }
  };

  const [menuServicesTime, setMenuServicesTime] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (showModal) {
      services.getMenuServiceTime().then((response) => {
        setMenuServicesTime(response.data);
      });
    }
  }, [showModal]);

  const [menuWizard, setMenuWizard] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (showModal) {
      services.getMenuWizard().then((response) => {
        setMenuWizard(response.data);
      });
    }
  }, [showModal]);

  const handleChangeMenuProperty = (event: ChangeEvent<HTMLInputElement>) => {
    if (selectedMenu && menuPropertiesActive && menuPropertiesActive.length > 0) {
      const newPropsActive = menuPropertiesActive?.map((obj) => {
        if (obj.property === parseInt(event.currentTarget.value)) {
          return { ...obj, is_active: event.currentTarget.checked };
        }
        return obj;
      });
      setMenuPropertiesActive(newPropsActive);
      setSelectedMenu({ ...selectedMenu, menu_properties: newPropsActive, img: menuImgName.current });
    }
  };
  const handleChangeMenuDisplayTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      const displayTiming = parseInt(event.currentTarget.value);
      setSelectedMenu({
        ...selectedMenu,
        display_timing: displayTiming,
        is_disable_control_group: displayTiming === 6 ? false : true,
        img: menuImgName.current,
      });
    }
  };

  const handleChangeMenuSubDisplayTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_display_timing: parseInt(event.currentTarget.value),
      });
    }
  };

  const handleChangeOverlayMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        overlay_group: parseInt(event.currentTarget.value),
      });
      setOverlayGroupError(false);
    }
  };

  const handleChangeSubOverlayMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_overlay_group: parseInt(event.currentTarget.value),
      });
      setSubOverlayGroupError(false);
    }
  };

  const handleResFromUploadImgModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      menuImgName.current = filename;
      setSelectedMenu({ ...selectedMenu, img: filename });
    }
  };

  // Promotional image upload handlers
  const handleResFromImg1x2UploadModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      const fieldName = currentImgLang ? `img_1x2_${currentImgLang}` : 'img_1x2';
      img1x2Name.current = filename;
      setSelectedMenu({ ...selectedMenu, [fieldName]: filename });
    }
  };

  const handleResFromImg2x1UploadModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      const fieldName = currentImgLang ? `img_2x1_${currentImgLang}` : 'img_2x1';
      img2x1Name.current = filename;
      setSelectedMenu({ ...selectedMenu, [fieldName]: filename });
    }
  };

  const handleResFromImg2x2UploadModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      const fieldName = currentImgLang ? `img_2x2_${currentImgLang}` : 'img_2x2';
      img2x2Name.current = filename;
      setSelectedMenu({ ...selectedMenu, [fieldName]: filename });
    }
  };

  const handleResFromImg3x1UploadModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      const fieldName = currentImgLang ? `img_3x1_${currentImgLang}` : 'img_3x1';
      img3x1Name.current = filename;
      setSelectedMenu({ ...selectedMenu, [fieldName]: filename });
    }
  };

  const handleResFromImg3x2UploadModal = (filename: string, image_path: string) => {
    if (selectedMenu) {
      const fieldName = currentImgLang ? `img_3x2_${currentImgLang}` : 'img_3x2';
      img3x2Name.current = filename;
      setSelectedMenu({ ...selectedMenu, [fieldName]: filename });
    }
  };

  const [overlayGroupError, setOverlayGroupError] = useState<boolean>(false);
  const [subOverlayGroupError, setSubOverlayGroupError] = useState<boolean>(false);
  const handleSaveMenuUpdate = async () => {
    if (selectedMenu) {
      // validate required root image for menu
      if (!menuImgName.current || menuImgName.current.trim() === '') {
        showGlobalModal &&
          showGlobalModal(() => <AlertModal title={t('App.warning')} message="メニュー画像が登録されていません。" />);
        return;
      }

      const menuCloneObject = { ...selectedMenu };
      menuCloneObject.img = menuCloneObject.img || '';
      await menuDetailService.patchMenu(menuCloneObject).then((res) => {
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              title={t('menuDetailFeature.Form.title_success')}
              message={t('menuDetailFeature.Form.save_success')}
              onClose={() => handleCloseModal(true)}
            ></AlertModal>
          ));
      });
    }
  };

  const handleCloseModal = (is_update = false) => {
    if (is_update === true && props.menuCallBack !== undefined) {
      const menuCloneObject = { ...selectedMenu, img: menuImgName.current };
      props.menuCallBack(menuCloneObject);
    }
    if (props.onMenuModalClose !== undefined) {
      props.onMenuModalClose();
    }
    setShowModal(false);
    setOverlayGroupError(false);
    setSubOverlayGroupError(false);
  };

  useEscapeKey(handleCloseModal);

  // const getSvgDataUrl = useCallback(() => {
  //   const SvgComponent = React.memo(() => (
  //     <NoImageIcon
  //       width={150}
  //       height={100}
  //       display_txt={`${IMAGE_SIZES.menu_img.minWidth}x${IMAGE_SIZES.menu_img.minHeight}~${IMAGE_SIZES.menu_img.maxWidth}x${IMAGE_SIZES.menu_img.maxHeight}`}
  //     />
  //   ));
  //   const svgString = ReactDOMServer.renderToString(<SvgComponent />);
  //   return svgToDataURL(svgString);
  // }, []);

  // const getPromoSvgDataUrl = useCallback((size: '1x2' | '2x1' | '2x2' | '3x1' | '3x2') => {
  //   const sizeConfig = {
  //     '1x2': { width: 132, height: 209, config: IMAGE_SIZES.promo_img_1x2 },
  //     '2x1': { width: 270, height: 88, config: IMAGE_SIZES.promo_img_2x1 },
  //     '2x2': { width: 266, height: 206, config: IMAGE_SIZES.promo_img_2x2 },
  //     '3x1': { width: 409, height: 88, config: IMAGE_SIZES.promo_img_3x1 },
  //     '3x2': { width: 404, height: 206, config: IMAGE_SIZES.promo_img_3x2 },
  //   };
  //   const { width, height, config } = sizeConfig[size];
  //   const displayText = `${config.minWidth}x${config.minHeight}~${config.maxWidth}x${config.maxHeight}`;
  //   const SvgComponent = React.memo(() => <NoImageIcon width={width} height={height} display_txt={displayText} />);
  //   const svgString = ReactDOMServer.renderToString(<SvgComponent />);
  //   return svgToDataURL(svgString);
  // }, []);

  const handleMultiSelectChangeControlGroupMenu = (selectedValues: number[]) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        display_control_groups: selectedValues,
      });
    }
  };

  const handleMultiSelectChangeSubControlGroupMenu = (selectedValues: number[]) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_display_control_groups: selectedValues,
      });
    }
  };

  const handleChangeOrderQuantityLimitType = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        order_quantity_limit_type: parseInt(event.currentTarget.value),
      });
    }
  };

  const [overlayGroups, setOverlayGroups] = useState<Overlay[]>([]);
  useEffect(() => {
    if (showModal) {
      services.getOverlayGroups().then((res) => {
        const data = res.data.filter((item: Overlay) => !item.delete_flg);
        data && setOverlayGroups(data);
      });
    }
  }, [showModal]);

  const [listDisplayControlGroup, setDisplayControlGroup] = useState<DisplayControlGroupType[]>([]);
  useEffect(() => {
    if (showModal) {
      services.getDisplayControlGroups().then((res) => {
        const data: DisplayControlGroupType[] = res.data
          .filter((item: DisplayControlGroup) => !item.delete_flg)
          .map((item: DisplayControlGroup) => ({
            value: item.id,
            key: item.name,
            display_name: item.display_name,
          }));
        setDisplayControlGroup(data);
      });
    }
  }, [showModal]);

  const [listMenuOrderQuantityLimit, setListMenuOrderQuantityLimit] = useState<{ key: string; value: string }[]>([]);
  useEffect(() => {
    if (showModal) {
      services.getListOrderQuantityLimit().then((res) => {
        setListMenuOrderQuantityLimit(res.data);
      });
    }
  }, [showModal]);

  return (
    <>
      {showImgUploadModal && (
        <ImageUploadModal
          imagepath={menuImgName.current ? process.env.REACT_APP_IMAGE_URL + menuImgName.current : ''}
          minWidth={IMAGE_SIZES.menu_img.minWidth}
          minHeight={IMAGE_SIZES.menu_img.minHeight}
          maxWidth={IMAGE_SIZES.menu_img.maxWidth}
          maxHeight={IMAGE_SIZES.menu_img.maxHeight}
          maxFileSize={IMAGE_SIZES.menu_img.maxFileSize}
          onReflect={handleResFromUploadImgModal}
          onClose={() => {
            setShowImgUploadModal(false);
          }}
        ></ImageUploadModal>
      )}
      {showImg1x2UploadModal && (
        <ImageUploadModal
          imagepath={img1x2Name.current ? process.env.REACT_APP_IMAGE_URL + img1x2Name.current : ''}
          minWidth={IMAGE_SIZES.promo_img_1x2.minWidth}
          minHeight={IMAGE_SIZES.promo_img_1x2.minHeight}
          maxWidth={IMAGE_SIZES.promo_img_1x2.maxWidth}
          maxHeight={IMAGE_SIZES.promo_img_1x2.maxHeight}
          maxFileSize={IMAGE_SIZES.promo_img_1x2.maxFileSize}
          onReflect={handleResFromImg1x2UploadModal}
          onClose={() => {
            setShowImg1x2UploadModal(false);
            setCurrentImgLang('');
          }}
        ></ImageUploadModal>
      )}
      {showImg2x1UploadModal && (
        <ImageUploadModal
          imagepath={img2x1Name.current ? process.env.REACT_APP_IMAGE_URL + img2x1Name.current : ''}
          minWidth={IMAGE_SIZES.promo_img_2x1.minWidth}
          minHeight={IMAGE_SIZES.promo_img_2x1.minHeight}
          maxWidth={IMAGE_SIZES.promo_img_2x1.maxWidth}
          maxHeight={IMAGE_SIZES.promo_img_2x1.maxHeight}
          maxFileSize={IMAGE_SIZES.promo_img_2x1.maxFileSize}
          onReflect={handleResFromImg2x1UploadModal}
          onClose={() => {
            setShowImg2x1UploadModal(false);
            setCurrentImgLang('');
          }}
        ></ImageUploadModal>
      )}
      {showImg2x2UploadModal && (
        <ImageUploadModal
          imagepath={img2x2Name.current ? process.env.REACT_APP_IMAGE_URL + img2x2Name.current : ''}
          minWidth={IMAGE_SIZES.promo_img_2x2.minWidth}
          minHeight={IMAGE_SIZES.promo_img_2x2.minHeight}
          maxWidth={IMAGE_SIZES.promo_img_2x2.maxWidth}
          maxHeight={IMAGE_SIZES.promo_img_2x2.maxHeight}
          maxFileSize={IMAGE_SIZES.promo_img_2x2.maxFileSize}
          onReflect={handleResFromImg2x2UploadModal}
          onClose={() => {
            setShowImg2x2UploadModal(false);
            setCurrentImgLang('');
          }}
        ></ImageUploadModal>
      )}
      {showImg3x1UploadModal && (
        <ImageUploadModal
          imagepath={img3x1Name.current ? process.env.REACT_APP_IMAGE_URL + img3x1Name.current : ''}
          minWidth={IMAGE_SIZES.promo_img_3x1.minWidth}
          minHeight={IMAGE_SIZES.promo_img_3x1.minHeight}
          maxWidth={IMAGE_SIZES.promo_img_3x1.maxWidth}
          maxHeight={IMAGE_SIZES.promo_img_3x1.maxHeight}
          maxFileSize={IMAGE_SIZES.promo_img_3x1.maxFileSize}
          onReflect={handleResFromImg3x1UploadModal}
          onClose={() => {
            setShowImg3x1UploadModal(false);
            setCurrentImgLang('');
          }}
        ></ImageUploadModal>
      )}
      {showImg3x2UploadModal && (
        <ImageUploadModal
          imagepath={img3x2Name.current ? process.env.REACT_APP_IMAGE_URL + img3x2Name.current : ''}
          minWidth={IMAGE_SIZES.promo_img_3x2.minWidth}
          minHeight={IMAGE_SIZES.promo_img_3x2.minHeight}
          maxWidth={IMAGE_SIZES.promo_img_3x2.maxWidth}
          maxHeight={IMAGE_SIZES.promo_img_3x2.maxHeight}
          maxFileSize={IMAGE_SIZES.promo_img_3x2.maxFileSize}
          onReflect={handleResFromImg3x2UploadModal}
          onClose={() => {
            setShowImg3x2UploadModal(false);
            setCurrentImgLang('');
          }}
        ></ImageUploadModal>
      )}
      <div className="static-modal">
        <Modal
          // dialogClassName="menu-detail-modal"
          animation={true}
          fullscreen={true}
          centered={true}
          show={showModal}
          backdrop={'static'}
          keyboard={false}
        >
          <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <div className="row">
              <div className=" col-sm-5">
                <FeatureTitleBar title={t('menuDetailFeature.Text.menu_detail_normal')}></FeatureTitleBar>
              </div>
              <div className="col text-end">
                <button type="button" className="btn-close" onClick={() => handleCloseModal()}></button>
              </div>
            </div>
            <Row className="mb-3">
              <div className="d-flex align-items-end" style={{ justifyContent: 'right' }}>
                <button onClick={handleSaveMenuUpdate} type="button" className="btn btn--dark-green">
                  {t('menuDetailFeature.Component.save_btn')}
                </button>
              </div>
            </Row>
            <Row className="mb-3">
              <Col className="col-sm-6 col-xs-12">
                <div className="mb-3 d-flex justify-content-between">
                  <div>
                    <h5>{t('menuDetailFeature.Text.menu_settings')}</h5>
                    <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.notice_text') }}></div>
                  </div>
                </div>
                <div>
                  <Table className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr className="mb-3">
                        <td className="table-primary col-md-4" colSpan={3}>
                          {t('menuDetailFeature.Text.menu_name')}
                        </td>
                        <td className="col-md-6">{selectedMenu?.food_system_menu?.name || ''}</td>
                      </tr>
                      <tr className="mb-3">
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.display_timing')}
                        </td>
                        <td>
                          <div className="row justify-content-center align-items-center">
                            <div className="col-xs-4 col-sm-4">
                              <span>{t('menuDetailFeature.Text.control_condition')}</span>
                            </div>
                            <div className="col-xs-8 col-sm-8">
                              <Form.Select
                                onChange={handleChangeMenuDisplayTiming}
                                defaultValue={selectedMenu?.default_value_display_timing}
                              >
                                {MENU_DISPLAY_TIMING.map((condition, index) => {
                                  return (
                                    <option key={index} value={condition.value}>
                                      {condition.label}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </div>
                          </div>

                          <div className="row align-items-center mt-3">
                            <div className="col-xs-4 col-sm-4">
                              <div
                                dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.display_control_group') }}
                              ></div>
                            </div>
                            <div className="col-xs-8 col-sm-8 mb-3">
                              <MultiSelectDropdown
                                defaultText={'選択無し'}
                                options={listDisplayControlGroup}
                                selectedValues={selectedMenu?.display_control_groups || []}
                                onChange={handleMultiSelectChangeControlGroupMenu}
                                isDisabled={selectedMenu?.is_disable_control_group}
                              />
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note3') }}></div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.overlay_group')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Select
                                className="text-center"
                                onChange={handleChangeOverlayMenu}
                                defaultValue={selectedMenu?.overlay_group || 0}
                              >
                                <option value="0">{t('menuDetailFeature.Text.default_overlay_group')}</option>
                                {overlayGroups.map((overlay, index) => {
                                  return (
                                    <option key={index} value={overlay.id}>
                                      {overlay.name}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                              <div style={{ textAlign: 'left' }}>
                                {overlayGroupError && (
                                  <span className="form-control-feedback">{t('menuDetailFeature.Form.required')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.menu_property')}
                        </td>
                        <td>
                          <div className="row">
                            <Col className="col-sm-12 col-xs-12 mb-3">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.provide_content')}
                                </legend>
                                <Row className="mb-1">
                                  {(() => {
                                    const half = Math.ceil(propertiesProvide.length / 2);
                                    const leftColumn = propertiesProvide.slice(0, half);
                                    const rightColumn = propertiesProvide.slice(half);
                                    return (
                                      <>
                                        <Col sm={6}>
                                          {leftColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`provide_property_left_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                        <Col sm={6}>
                                          {rightColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`provide_property_right_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                      </>
                                    );
                                  })()}
                                </Row>
                              </fieldset>
                            </Col>
                            <Col className="col-sm-12 col-xs-12 mb-3">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.limit_and_option')}
                                </legend>
                                <Row className="mb-1">
                                  {(() => {
                                    const half = Math.ceil(propertiesLimit.length / 2);
                                    const leftColumn = propertiesLimit.slice(0, half);
                                    const rightColumn = propertiesLimit.slice(half);
                                    return (
                                      <>
                                        <Col sm={6}>
                                          {leftColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`limit_property_left_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                        <Col sm={6}>
                                          {rightColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`limit_property_right_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                      </>
                                    );
                                  })()}
                                </Row>
                              </fieldset>
                            </Col>
                            <Col className="col-sm-12 col-xs-12 mb-3 text-center">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t(
                                    selectedMenu?.timing_flg
                                      ? 'menuDetailFeature.Text.limit_and_option_enabled'
                                      : 'menuDetailFeature.Text.limit_and_option_disabled'
                                  )}
                                </legend>
                                <Row className="mb-3 mt-3">
                                  <Col sm={9} className="ms-3 text-center">
                                    <Form.Select
                                      disabled={!selectedMenu?.timing_flg}
                                      onChange={handleChangeServiceTiming}
                                      value={selectedMenu?.timing_id ?? ''}
                                      id="menu_timing"
                                    >
                                      {selectedMenu?.timing_flg && (
                                        <option value="">{t('menuDetailFeature.Text.hide_option')}</option>
                                      )}
                                      {(!selectedMenu?.timing_flg || selectedMenu?.timing_flg === null) && (
                                        <option>{t('menuDetailFeature.Text.invalid')}</option>
                                      )}
                                      {menuServicesTime.map((service) => (
                                        <option key={service.id} value={service.id}>
                                          {selectedMenu?.timing_flg ? service.name : ''}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                            <Col className="col-sm-12 col-xs-12 mb-3 text-center">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.wizard_title')}
                                </legend>
                                <Row className="mb-3 mt-3">
                                  <Col sm={9} className="ms-3 text-center">
                                    <Form.Select
                                      onChange={handleChangeWizard}
                                      value={selectedMenu?.wizard_id ?? ''}
                                      id="menu_wizard"
                                    >
                                      <option value="">{t('menuDetailFeature.Text.hide_option')}</option>
                                      {menuWizard.map((service) => (
                                        <option key={service.id} value={service.id}>
                                          {service.name}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.order_limit_type')}
                        </td>
                        <td>
                          <div className="row mb-3 d-flex">
                            <div
                              style={{ textAlign: 'left' }}
                              dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note4') }}
                            ></div>

                            <div className="text-center justify-content-around mt-3 mb-3">
                              <Form.Select
                                className="text-center"
                                onChange={handleChangeOrderQuantityLimitType}
                                defaultValue={selectedMenu?.order_quantity_limit_type || 0}
                              >
                                <option value="0">{t('menuDetailFeature.Text.default_order_limit')}</option>
                                {listMenuOrderQuantityLimit.map((order, index) => {
                                  return (
                                    <option key={index} value={order.key}>
                                      {order.value}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.menu_image')}
                        </td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {menuImgName.current.length > 0 && (
                                <Image
                                  style={{
                                    maxHeight: '100px',
                                  }}
                                  src={process.env.REACT_APP_IMAGE_URL + menuImgName.current}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                onClick={() => {
                                  setShowImgUploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.display_name')}
                        </td>
                        <td className="table-primary" rowSpan={6}></td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <Form.Control
                            name="display_name_ja"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_ja ? selectedMenu.display_name_ja : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <Form.Control
                            name="display_name_en"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_en ? selectedMenu.display_name_en : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <Form.Control
                            name="display_name_zh_hans"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_zh_hans ? selectedMenu.display_name_zh_hans : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <Form.Control
                            name="display_name_zh_hant"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_zh_hant ? selectedMenu.display_name_zh_hant : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <Form.Control
                            name="display_name_ko"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_ko ? selectedMenu.display_name_ko : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <Form.Control
                            name="display_name_vi"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.display_name_vi ? selectedMenu.display_name_vi : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.explanation')}
                        </td>
                        <td className="table-primary" rowSpan={6}></td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <textarea
                            className="form-control"
                            rows={2}
                            name="explanation_ja"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_ja ? selectedMenu.explanation_ja : ''}
                          ></textarea>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <textarea
                            className="form-control"
                            rows={2}
                            name="explanation_en"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_en ? selectedMenu.explanation_en : ''}
                          ></textarea>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <textarea
                            className="form-control"
                            rows={2}
                            name="explanation_zh_hans"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_zh_hans ? selectedMenu.explanation_zh_hans : ''}
                          ></textarea>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <textarea
                            className="form-control"
                            rows={2}
                            name="explanation_zh_hant"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_zh_hant ? selectedMenu.explanation_zh_hant : ''}
                          ></textarea>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <textarea
                            className="form-control"
                            rows={2}
                            name="explanation_ko"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_ko ? selectedMenu.explanation_ko : ''}
                          ></textarea>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <textarea
                            rows={2}
                            className="form-control"
                            name="explanation_vi"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.explanation_vi ? selectedMenu.explanation_vi : ''}
                          ></textarea>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.note1')}
                        </td>
                        <td className="table-primary" rowSpan={6}></td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <Form.Control
                            name="note1_ja"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_ja ? selectedMenu.note1_ja : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <Form.Control
                            name="note1_en"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_en ? selectedMenu.note1_en : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <Form.Control
                            name="note1_zh_hans"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_zh_hans ? selectedMenu.note1_zh_hans : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <Form.Control
                            name="note1_zh_hant"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_zh_hant ? selectedMenu.note1_zh_hant : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <Form.Control
                            name="note1_ko"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_ko ? selectedMenu.note1_ko : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <Form.Control
                            name="note1_vi"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note1_vi ? selectedMenu.note1_vi : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.note2')}
                        </td>
                        <td className="table-primary" rowSpan={6}></td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <Form.Control
                            name="note2_ja"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_ja ? selectedMenu.note2_ja : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <Form.Control
                            name="note2_en"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_en ? selectedMenu.note2_en : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <Form.Control
                            name="note2_zh_hans"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_zh_hans ? selectedMenu.note2_zh_hans : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <Form.Control
                            name="note2_zh_hant"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_zh_hant ? selectedMenu.note2_zh_hant : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <Form.Control
                            name="note2_ko"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_ko ? selectedMenu.note2_ko : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <Form.Control
                            name="note2_vi"
                            onChange={handleChangeMenuAttribute}
                            defaultValue={selectedMenu?.note2_vi ? selectedMenu.note2_vi : ''}
                          ></Form.Control>
                        </td>
                      </tr>
                      {/* Promotional Image 1x2 */}
                      <tr>
                        <td className="table-primary" rowSpan={30}>
                          <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.promotional_image') }} />
                        </td>
                        <td className="table-primary" rowSpan={6}>
                          1x2
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_ja && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_ja}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ja');
                                  img1x2Name.current = selectedMenu?.img_1x2_ja || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_en && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_en}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('en');
                                  img1x2Name.current = selectedMenu?.img_1x2_en || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_zh_hans && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_zh_hans}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hans');
                                  img1x2Name.current = selectedMenu?.img_1x2_zh_hans || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_zh_hant && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_zh_hant}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hant');
                                  img1x2Name.current = selectedMenu?.img_1x2_zh_hant || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_ko && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_ko}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ko');
                                  img1x2Name.current = selectedMenu?.img_1x2_ko || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_1x2_vi && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_1x2_vi}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('vi');
                                  img1x2Name.current = selectedMenu?.img_1x2_vi || '';
                                  setShowImg1x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Promotional Image 2x1 */}
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          2x1
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_ja && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_ja}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ja');
                                  img2x1Name.current = selectedMenu?.img_2x1_ja || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_en && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_en}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('en');
                                  img2x1Name.current = selectedMenu?.img_2x1_en || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_zh_hans && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_zh_hans}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hans');
                                  img2x1Name.current = selectedMenu?.img_2x1_zh_hans || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_zh_hant && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_zh_hant}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hant');
                                  img2x1Name.current = selectedMenu?.img_2x1_zh_hant || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_ko && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_ko}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ko');
                                  img2x1Name.current = selectedMenu?.img_2x1_ko || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x1_vi && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x1_vi}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('vi');
                                  img2x1Name.current = selectedMenu?.img_2x1_vi || '';
                                  setShowImg2x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Promotional Image 2x2 */}
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          2x2
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_ja && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_ja}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ja');
                                  img2x2Name.current = selectedMenu?.img_2x2_ja || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_en && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_en}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('en');
                                  img2x2Name.current = selectedMenu?.img_2x2_en || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_zh_hans && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_zh_hans}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hans');
                                  img2x2Name.current = selectedMenu?.img_2x2_zh_hans || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_zh_hant && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_zh_hant}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hant');
                                  img2x2Name.current = selectedMenu?.img_2x2_zh_hant || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_ko && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_ko}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ko');
                                  img2x2Name.current = selectedMenu?.img_2x2_ko || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_2x2_vi && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_2x2_vi}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('vi');
                                  img2x2Name.current = selectedMenu?.img_2x2_vi || '';
                                  setShowImg2x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Promotional Image 3x1 */}
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          3x1
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_ja && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_ja}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ja');
                                  img3x1Name.current = selectedMenu?.img_3x1_ja || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_en && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_en}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('en');
                                  img3x1Name.current = selectedMenu?.img_3x1_en || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_zh_hans && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_zh_hans}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hans');
                                  img3x1Name.current = selectedMenu?.img_3x1_zh_hans || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_zh_hant && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_zh_hant}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hant');
                                  img3x1Name.current = selectedMenu?.img_3x1_zh_hant || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_ko && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_ko}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ko');
                                  img3x1Name.current = selectedMenu?.img_3x1_ko || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x1_vi && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x1_vi}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('vi');
                                  img3x1Name.current = selectedMenu?.img_3x1_vi || '';
                                  setShowImg3x1UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Promotional Image 3x2 */}
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          3x2
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_ja && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_ja}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ja');
                                  img3x2Name.current = selectedMenu?.img_3x2_ja || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_en && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_en}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('en');
                                  img3x2Name.current = selectedMenu?.img_3x2_en || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_zh_hans && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_zh_hans}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hans');
                                  img3x2Name.current = selectedMenu?.img_3x2_zh_hans || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_zh_hant && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_zh_hant}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('zh_hant');
                                  img3x2Name.current = selectedMenu?.img_3x2_zh_hant || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_ko && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_ko}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('ko');
                                  img3x2Name.current = selectedMenu?.img_3x2_ko || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="d-flex gap-3 justify-content-between">
                            <div className="position-relative d-inline-block">
                              {selectedMenu?.img_3x2_vi && (
                                <Image
                                  style={{ maxHeight: 200 }}
                                  src={process.env.REACT_APP_IMAGE_URL + selectedMenu.img_3x2_vi}
                                  alt="No Image"
                                />
                              )}
                            </div>
                            <div className="d-flex align-items-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCurrentImgLang('vi');
                                  img3x2Name.current = selectedMenu?.img_3x2_vi || '';
                                  setShowImg3x2UploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={3}>
                          {t('menuDetailFeature.Text.remarks')}
                        </td>
                        <td>
                          <div className="row">
                            <div className="col-xs-12">
                              <textarea
                                name="note"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note ? selectedMenu.note : ''}
                                className="form-control"
                                rows={4}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Col>
              <Col className="col-sm-6 col-xs-12">
                <div className="mb-3">
                  <h5>{t('menuDetailFeature.Text.sub_menu_settings')}</h5>
                  <Table className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr>
                        <td className="table-primary col-md-4" colSpan={2}>
                          {t('menuDetailFeature.Text.sub_menu_property')}
                        </td>
                        <td>{selectedMenu?.is_sub ? '〇' : 'X'}</td>
                      </tr>
                      <tr>
                        <td className="table-primary col-md-6" colSpan={2}>
                          {t('menuDetailFeature.Text.display_timing')}
                        </td>
                        <td>
                          <div className="row justify-content-center align-items-center">
                            <div className="col-xs-5 col-sm-5">
                              <span>{t('menuDetailFeature.Text.control_condition')}</span>
                            </div>
                            <div className="col-xs-7 col-sm-7">
                              <Form.Select
                                onChange={handleChangeMenuSubDisplayTiming}
                                defaultValue={selectedMenu?.sub_display_timing}
                              >
                                {MENU_DISPLAY_TIMING_SUB.map((condition, index) => {
                                  return (
                                    <option key={index} value={condition.value}>
                                      {condition.key}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </div>
                          </div>

                          <div className="row align-items-center mt-3">
                            <div className="col-xs-5 col-sm-5">
                              <div
                                dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.display_control_group') }}
                              ></div>
                            </div>
                            <div className="col-xs-7 col-sm-7 mb-3">
                              <MultiSelectDropdown
                                defaultText={'選択無し'}
                                options={listDisplayControlGroup}
                                selectedValues={selectedMenu?.sub_display_control_groups || []}
                                onChange={handleMultiSelectChangeSubControlGroupMenu}
                                isDisabled={selectedMenu?.sub_display_timing !== 6}
                              />
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note3') }}></div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.overlay_group')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Select
                                className="text-center"
                                onChange={handleChangeSubOverlayMenu}
                                defaultValue={selectedMenu?.sub_overlay_group || 0}
                              >
                                <option value="0">{t('menuDetailFeature.Text.default_overlay_group')}</option>
                              </Form.Select>
                              <div style={{ textAlign: 'left' }}>
                                {subOverlayGroupError && (
                                  <span className="form-control-feedback">{t('menuDetailFeature.Form.required')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  <div className="mb-3" style={{ marginBottom: '20px' }}></div>
                  <div className="mb-3">
                    <h5>{t('menuDetailFeature.Text.register_information')}</h5>
                    <Table className="table table-bordered text-center align-middle mb-3">
                      <tbody>
                        <tr>
                          <td rowSpan={2} className="col-md-4" style={{ backgroundColor: '#CFC9C9' }}>
                            {t('menuDetailFeature.Table.date_of_expired')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.start')}</td>
                          <td>{menuView?.startDate || '-'}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.end')}</td>
                          <td>{menuView?.endDate || '-'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div>
                    <Table className="table table-bordered text-center align-middle mb-3">
                      <thead>
                        <tr className="align-middle">
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            {t('menuDetailFeature.Table.pos_cd')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            {t('menuDetailFeature.Table.pos_name')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: t('menuDetailFeature.Table.amount_of_money_excluding_tax'),
                              }}
                            ></div>
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} colSpan={2}>
                            {t('menuDetailFeature.Table.normal')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} colSpan={2}>
                            {t('menuDetailFeature.Table.sub')}
                          </td>
                        </tr>
                        <tr className="align-middle">
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.normal')}</td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: t('menuDetailFeature.Table.normal_price_including_tax'),
                              }}
                            ></div>
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.sub')}</td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>
                            <div
                              dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Table.sub_price_including_tax') }}
                            ></div>
                          </td>
                        </tr>
                      </thead>
                      <tbody>
                        {menuDetails &&
                          menuDetails.map((menu_detail, index) => {
                            return (
                              <tr key={index}>
                                <td>{menu_detail.pos_code.cd}</td>
                                <td>{menu_detail.name}</td>
                                <td>
                                  {getCurrency(menu_detail.standard_price) ?? t('menuDetailFeature.Text.not_obtained')}
                                </td>
                                <td>{menu_detail.pos_code.normal_flg ? '〇' : 'ー'}</td>
                                <td>
                                  {menu_detail.pos_code.main_price
                                    ? getCurrency(menu_detail.pos_code.main_price)
                                    : '未取得'}
                                </td>
                                <td>{menu_detail.pos_code.sub_flg ? '〇' : 'ー'}</td>
                                <td>
                                  {menu_detail.pos_code.composition_price
                                    ? getCurrency(menu_detail.pos_code.composition_price)
                                    : '未取得'}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <button className="btn btn--red" onClick={() => handleCloseModal()}>
              {t('menuDetailFeature.Component.close')}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
});

export default MenuDetailModal;
